import {
  Component,
  type ElementRef,
  effect,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { TVDFormField } from '@tvd/design-system/form-field';
import { Icon } from '@tvd/design-system/icon';
import { Input } from '@tvd/design-system/input';

@Component({
  selector: 'bottom-bar-search',
  imports: [TVDFormField, Input, TranslatePipe, Icon, FormField],
  host: {
    class: 'block w-full',
    '(click)': '$event.stopPropagation()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="bg-card">
      <div class="p-4 space-y-3 pt-6">
        <!-- Search Input -->
        <div class="relative">
          <tvd-icon
            icon="search"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/60"
          />

          <tvd-form-field>
            <input
              tvdInput
              #searchInput
              type="text"
              [formField]="form.query"
              [placeholder]="'placeholder.searchQuery' | translate"
            />
          </tvd-form-field>

          @if (form.query().value()) {
            <button
              type="button"
              (click)="clearSearch()"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/60 hover:text-foreground transition-colors"
            >
              <tvd-icon icon="close" />
            </button>
          }
        </div>

        <!-- Quick Actions or Results -->
        @if (form.query().value()) {
          <div class="space-y-2 max-h-60 overflow-y-auto">
            <p class="text-sm text-foreground/60 px-2">
              Searching for "{{ form.query().value() }}"...
            </p>
            <!-- Add your search results here -->
          </div>
        } @else {
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              class="flex items-center gap-2 p-3 bg-background-50 hover:bg-background-100
                     rounded-lg transition-colors text-left"
            >
              <tvd-icon icon="person" class="text-primary" />
              <span class="text-sm text-foreground">Customers</span>
            </button>
            <button
              type="button"
              class="flex items-center gap-2 p-3 bg-background-50 hover:bg-background-100
                     rounded-lg transition-colors text-left"
            >
              <tvd-icon icon="event" class="text-primary" />
              <span class="text-sm text-foreground">Bookings</span>
            </button>
            <button
              type="button"
              class="flex items-center gap-2 p-3 bg-background-50 hover:bg-background-100
                     rounded-lg transition-colors text-left"
            >
              <tvd-icon icon="design_services" class="text-primary" />
              <span class="text-sm text-foreground">Services</span>
            </button>
            <button
              type="button"
              class="flex items-center gap-2 p-3 bg-background-50 hover:bg-background-100
                     rounded-lg transition-colors text-left"
            >
              <tvd-icon icon="receipt_long" class="text-primary" />
              <span class="text-sm text-foreground">Invoices</span>
            </button>
          </div>
        }
      </div>
    </div>
  `,
})
export class BottomBarSearchComponent {
  readonly close = output<void>();

  readonly #model = signal({ query: '' });
  readonly form = form(this.#model);

  private readonly inputRef =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');

  constructor() {
    // Auto-focus input when component is created
    effect(() => {
      const input = this.inputRef()?.nativeElement;
      if (input) {
        requestAnimationFrame(() => input.focus());
      }
    });
  }

  clearSearch() {
    this.form.query().value.set('');
    this.inputRef()?.nativeElement.focus();
  }
}
